/*
  CART ACTIONS: one way into the cart, and a check before checkout.

  ONE WAY IN. The gift bar, the bag sync and the bag's upsells each used to
  call /cart/add.js, /cart/change.js and /cart/update.js on their own. Two of
  them writing at the same moment (the gift bar correcting gifts while the bag
  sync applies another device's change, say) read the same bag, both wrote,
  and one undid the other. AltenewCart.post() puts every write through one
  queue, so each starts only when the last has finished and reads the bag the
  last one left. Callers keep their own events and error handling: post()
  resolves with the same Response fetch() would.

  A CHECK BEFORE CHECKOUT. Offers end, apps get switched off, a bag sits in a
  tab for a week, and the bag sync carries lines between devices. So a bag can
  reach checkout holding things that no longer apply:

    - a gift or offer line that is no longer getting its discount, so the
      shopper would pay full price for something promised free or cheap
      (three orders were charged for a "free" gift this way, 09/17/2026);
    - a gift from an offer that has been retired but whose app still adds it
      (Rebuy's old tiered progress bar kept giving the Ladybug Charm Stamp Set
      away for a week after the site stopped offering it, 90 orders).

  clean() takes those out, one write, and tells the shopper in a short notice.
  It runs when the bag is opened, on the cart page, a moment after the bag
  changes, and always right before checkout (the checkout button, the cart
  page form, and Buy Now through beforeCheckout()). Express buttons (Shop Pay,
  PayPal) cannot be held back, which is why the check also runs whenever the
  bag is shown: by the time those buttons can be pressed it has already run.

  WHAT COUNTS AS A GIFT OR OFFER LINE. Only a line an offer put there, told by
  the marks it carries:
    _free_gift               our tier gifts (free-gift-bar.js)
    _pdp_offer               the product-page pop-up offers
    _gwp_threshold           Rebuy's tiered progress bar (retired, see below)
    _rsf_bxgy_gy / _flow_id  Rebuy Smart Flow "buy X get Y" gifts
    _attribution "... Gift ..." from Rebuy (Gift with Purchase)
  A Rebuy product suggestion carries _source: Rebuy too, but it is a normal
  purchase at full price, so _source alone never counts. Anything unmarked is
  the shopper's own choice and is never touched, even at $0.

  DISCOUNT CODES ARE LEFT ALONE. This check used to take out any code the
  cart reported as not applicable. But a code shared in a link or typed in
  before its products are in the bag is "not applicable" only until they are
  added, and Shopify applies it then. Taking it out lost the discount
  (MINDYLIVE15, a cardstock-only code, was stripped on the cardstock
  collection, 10/14/2026). Checkout ignores a code that does not apply, so
  nothing is gained by taking it out.

  Our own _free_gift lines are left to free-gift-bar.js except at checkout: it
  owns them, and it re-adds a gift it thinks is due, so taking one out
  anywhere else would start the two fighting.

  RETIRED MARKS. Theme settings > Cart > "Retired gift marks" lists marks
  whose lines are always taken out, free or not. It starts with
  _gwp_threshold, Rebuy's tiered bar, which the site replaced with its own
  gift bar. Empty the setting to stop that.

  LOOPS. An app can add back a line the moment it is taken out. The same line
  is taken out at most twice per bag outside checkout; after that it is left
  for the check at checkout, which always runs.
*/
(function () {
  'use strict';

  if (window.AltenewCart || typeof window.fetch !== 'function') return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var config = window.AltenewCartConfig || {};
  var ENABLED = config.enabled !== false;
  var RETIRED = (config.retiredMarks || []).filter(Boolean);
  // How long checkout waits for the check before going ahead anyway. A slow
  // cart must not keep a shopper from paying.
  var CHECKOUT_WAIT_MS = 3000;
  var SETTLE_MS = 1200;
  var MAX_REMOVALS = 2;
  var REMOVED_KEY = 'altenew-cart-check:removed';

  /* ---- One queue for every cart write ---- */

  var tail = Promise.resolve();

  function post(path, payload) {
    var run = function () {
      return fetch(ROOT + String(path).replace(/^\//, ''), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload || {})
      });
    };
    var result = tail.then(run, run);
    // The next write waits for this one, whether it worked or not.
    tail = result.then(function () {}, function () {});
    return result;
  }

  function read() {
    return fetch(ROOT + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    });
  }

  /* ---- Which lines an offer put in the bag ---- */

  function props(item) {
    return item.properties || {};
  }

  function marksOf(item) {
    var p = props(item);
    var marks = [];
    if (p._free_gift === 'true') marks.push('_free_gift');
    if (p._pdp_offer) marks.push('_pdp_offer');
    if (p._gwp_threshold) marks.push('_gwp_threshold');
    if (p._rsf_bxgy_gy || p._flow_id) marks.push('_rsf_bxgy_gy');
    if (p._source === 'Rebuy' && /gift/i.test(p._attribution || '')) marks.push('_rebuy_gift');
    RETIRED.forEach(function (mark) {
      if (p[mark] !== undefined && marks.indexOf(mark) === -1) marks.push(mark);
    });
    return marks;
  }

  // Whatever came off this line, from any discount.
  function discountOn(item) {
    var off = Number(item.total_discount) || 0;
    (item.line_level_discount_allocations || []).forEach(function (a) {
      off = Math.max(off, Number(a.amount) || 0);
    });
    return off;
  }

  // Why this line should come out, or '' if it stays.
  function staleReason(item, atCheckout) {
    var marks = marksOf(item);
    if (!marks.length) return '';
    for (var i = 0; i < marks.length; i++) {
      if (RETIRED.indexOf(marks[i]) !== -1) return 'retired';
    }
    var ours = marks.length === 1 && marks[0] === '_free_gift';
    if (ours && !atCheckout) return '';
    // A gift or offer with nothing taken off: its offer has ended or no longer
    // applies to this bag. A free gift only partly discounted is charged too.
    if (!discountOn(item)) return 'no-discount';
    if (marks.indexOf('_pdp_offer') === -1 && Number(item.final_line_price) > 0) return 'charged';
    return '';
  }

  /* ---- Remembering what was taken out, per bag ---- */

  function lineId(item) {
    return item.variant_id + '|' + marksOf(item).join(',');
  }

  function removedCounts(token) {
    try {
      var saved = JSON.parse(window.sessionStorage.getItem(REMOVED_KEY) || 'null');
      if (saved && saved.token === token) return saved.counts || {};
    } catch (e) { /* storage refused: no loop guard beyond this page */ }
    return {};
  }

  function rememberRemoved(token, counts) {
    try {
      window.sessionStorage.setItem(REMOVED_KEY, JSON.stringify({ token: token, counts: counts }));
    } catch (e) { /* storage refused */ }
  }

  /* ---- The check ---- */

  var running = null;

  function clean(options) {
    if (!ENABLED) return Promise.resolve({ removed: [], codes: [] });
    var atCheckout = !!(options && options.atCheckout);
    if (running && !atCheckout) return running;

    // Read only once every write already queued has landed. Reading earlier
    // judged a bag that was about to change: on the dev-client preview a code
    // applied in the same burst as three adds was missed by the first pass.
    var work = tail.then(read).then(function (cart) {
      var token = String(cart.token || '').split('?')[0];
      var counts = removedCounts(token);
      var updates = {};
      var removed = [];

      (cart.items || []).forEach(function (item) {
        var reason = staleReason(item, atCheckout);
        if (!reason) return;
        var id = lineId(item);
        if (!atCheckout && (counts[id] || 0) >= MAX_REMOVALS) return;
        counts[id] = (counts[id] || 0) + 1;
        updates[item.key] = 0;
        removed.push({ title: item.product_title, reason: reason, marks: marksOf(item) });
      });

      if (!removed.length) return { removed: [], codes: [] };

      rememberRemoved(token, counts);
      if (window.console) console.info('[cart-actions] taking out what no longer applies', { lines: removed });

      return post('cart/update.js', { updates: updates }).then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        document.dispatchEvent(new CustomEvent('cart:build'));
        var result = { removed: removed, codes: [] };
        if (!atCheckout) notify(result);
        return result;
      });
    }).catch(function (error) {
      if (window.console) console.warn('[cart-actions] check skipped', error);
      return { removed: [], codes: [] };
    }).then(function (result) {
      if (running === work) running = null;
      return result;
    });

    if (!atCheckout) running = work;
    return work;
  }

  // Never holds checkout longer than CHECKOUT_WAIT_MS.
  function beforeCheckout() {
    return Promise.race([
      clean({ atCheckout: true }),
      new Promise(function (resolve) { window.setTimeout(resolve, CHECKOUT_WAIT_MS); })
    ]);
  }

  /* ---- Telling the shopper ---- */

  var notice = null;
  var noticeTimer = null;

  // "A", "A and B", "A, B and C".
  function list(names) {
    return names.length < 2 ? names.join('') : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  function notify(result) {
    if (!document.body || !result.removed.length) return;
    var names = result.removed.map(function (r) { return r.title; });
    var parts = [(names.length === 1 ? names[0] + ' was' : list(names) + ' were') +
      ' part of an offer that has ended, so we took ' + (names.length === 1 ? 'it' : 'them') + ' out of your bag.'];

    if (!notice) {
      var style = document.createElement('style');
      style.textContent =
        '.cart-offer-notice{position:fixed;left:16px;right:16px;margin-left:auto;top:calc(16px + env(safe-area-inset-top,0px));z-index:2147483000;' +
        'max-width:380px;display:flex;align-items:flex-start;gap:8px;padding:12px 8px 12px 16px;border-radius:8px;background:#fff;color:#1d1d1d;' +
        'border:1px solid #e0d3b8;font:inherit;font-size:14px;line-height:1.4;box-shadow:0 6px 24px rgba(0,0,0,.18)}' +
        '.cart-offer-notice[hidden]{display:none}.cart-offer-notice p{flex:1;margin:0}' +
        '.cart-offer-notice button{min-width:44px;min-height:44px;border:0;background:transparent;color:#1d1d1d;font-size:20px;line-height:1;cursor:pointer;border-radius:6px}' +
        '.cart-offer-notice button:focus-visible{outline:2px solid #1d1d1d;outline-offset:2px}';
      document.head.appendChild(style);
      notice = document.createElement('div');
      notice.className = 'cart-offer-notice';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      notice.appendChild(document.createElement('p'));
      var close = document.createElement('button');
      close.type = 'button';
      close.setAttribute('aria-label', 'Dismiss');
      close.textContent = '×';
      close.addEventListener('click', function () { notice.hidden = true; });
      notice.appendChild(close);
      document.body.appendChild(notice);
    }
    notice.firstChild.textContent = parts.join(' ');
    notice.hidden = false;
    window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(function () { notice.hidden = true; }, 10000);
  }

  /* ---- When it runs ---- */

  var settleTimer = null;
  function soon() {
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(function () { clean(); }, SETTLE_MS);
  }

  // The bag being shown: the drawer opening, and the cart page itself.
  document.addEventListener('cart:open', soon);
  document.addEventListener('drawerOpen.CartDrawer', soon);
  // The bag changing, by anyone. Settles first, so an app that adds a gift
  // and then its discount is seen after both.
  document.addEventListener('cart:written', soon);
  document.addEventListener('cart:updated', soon);
  if (/\/cart\/?$/.test(window.location.pathname)) soon();

  /*
    Checkout. Capture phase on the document, like cart-request-guard.js (which
    is loaded first and so still runs first). The click or submit is held, the
    check runs, and the same form is submitted again through requestSubmit so
    the theme's own submit handling (the terms box) still runs. The second
    pass is let through by the flag.
  */
  var passing = false;

  function checkoutForm(el) {
    var button = el && el.closest && el.closest('.cart__checkout, [name="checkout"]');
    if (!button) return null;
    return { form: button.form || button.closest('form'), button: button };
  }

  function proceed(form, button) {
    passing = true;
    try {
      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(button && button.form === form ? button : undefined);
      } else if (form) {
        if (!form.querySelector('input[name="checkout"]')) {
          var hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'checkout';
          hidden.value = '1';
          form.appendChild(hidden);
        }
        form.submit();
      } else {
        window.location.href = ROOT + 'checkout';
      }
    } finally {
      window.setTimeout(function () { passing = false; }, 0);
    }
  }

  function hold(evt, form, button) {
    if (passing || !ENABLED) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    beforeCheckout().then(function () { proceed(form, button); });
  }

  document.addEventListener('click', function (evt) {
    var found = checkoutForm(evt.target);
    if (!found) return;
    hold(evt, found.form, found.button);
  }, true);

  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form || typeof form.querySelector !== 'function') return;
    var button = evt.submitter && evt.submitter.matches && evt.submitter.matches('.cart__checkout, [name="checkout"]')
      ? evt.submitter
      : form.querySelector('.cart__checkout, [name="checkout"]');
    var buyNow = form.querySelector('input[name="return_to"][value="/checkout"]');
    if (!button && !buyNow) return;
    hold(evt, form, button);
  }, true);

  window.AltenewCart = {
    post: post,
    read: read,
    add: function (items) { return post('cart/add.js', { items: items }); },
    change: function (key, quantity) { return post('cart/change.js', { id: key, quantity: quantity }); },
    update: function (payload) { return post('cart/update.js', payload); },
    clean: clean,
    beforeCheckout: beforeCheckout,
    isOfferLine: function (item) { return marksOf(item).length > 0; }
  };
}());
